import { useCallback, useEffect, useState } from "react";
import { get } from "@/utils/service";
import { notify } from "@/components/ui/dialogs";
import { apiMessage } from "@/utils/api";

export interface ListPagination { page: number; per_page: number; total: number; total_pages: number }

/** A paginated API list (?page, ?page_size, plus filters). Filters reset to page 1. */
export function useRemoteList<T>(url: string, filters: Record<string, string | undefined> = {}, perPage = 15) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<ListPagination>({ page: 1, per_page: perPage, total: 0, total_pages: 0 });
  const key = JSON.stringify(filters);

  useEffect(() => setPagination((p) => ({ ...p, page: 1 })), [key]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pagination.page), page_size: String(pagination.per_page) });
      Object.entries(JSON.parse(key) as Record<string, string | undefined>).forEach(([k, v]) => v && params.set(k, v));
      const r = await get<{ data: T[]; pagination: { total: number; total_pages: number } }>(`${url}${url.includes("?") ? "&" : "?"}${params}`);
      setRows(r.data);
      setPagination((p) => ({ ...p, total: r.pagination?.total ?? r.data.length, total_pages: r.pagination?.total_pages ?? 1 }));
    } catch (e) {
      notify(apiMessage(e, "Could not load this list."));
    } finally {
      setLoading(false);
    }
  }, [url, key, pagination.page, pagination.per_page]);

  useEffect(() => { reload(); }, [reload]);

  return {
    rows, setRows, loading, pagination, reload,
    setPage: (page: number) => setPagination((p) => ({ ...p, page })),
    setPerPage: (per_page: number) => setPagination((p) => ({ ...p, per_page, page: 1 })),
  };
}
